import React, { useId, useState } from "react";
import type { DateRangeFilter } from "../types";
import type { DateWindow } from "../utils/dateWindow";
import { windowLabel } from "../utils/dateWindow";

export type WindowMode = "rolling" | "fixed";

interface SaveViewPopoverProps {
  /** The window the page is on — decides which modes are on offer. */
  dateWindow?: DateWindow;
  /** What that window currently resolves to, i.e. what "fixed" would freeze. */
  resolvedRange?: DateRangeFilter;
  /**
   * Set when editing an existing view rather than naming a new one: its current
   * name, and whether the page is on a different slice than the one it holds.
   */
  editing?: { name: string; sliceDiffers: boolean };
  /**
   * `replaceSlice` is always true for a new view; for an edit it says whether
   * the view should adopt the page's slice or keep the one it already has.
   */
  onSave: (name: string, mode: WindowMode, replaceSlice: boolean) => void;
  onCancel: () => void;
}

/**
 * Names a view and decides whether its window rolls or is frozen — for a new
 * view, and for editing one that already exists.
 *
 * Rolling is only offered when the page's window *is* rolling: a hand-picked
 * absolute range has no duration to roll forward, so there is nothing to choose
 * between. On "all time" the question is meaningless and the control hides —
 * which keeps the popover a single field in the common case.
 *
 * Editing keeps rename and re-slice as separate switches. Adopting the page's
 * slice by default would mean fixing a typo silently rewrites what the view
 * points at, which is the loss that made editing worth having.
 *
 * The window modes follow the slice: they appear whenever the view is going to
 * take the page's, which is either because the edit asked for it or because the
 * page is already on that slice. That second case is what lets an open rolling
 * view be frozen into a baseline without touching its filters; while the view
 * is keeping a slice you are *not* looking at, the page's window says nothing
 * about it and there is no question to ask.
 */
export function SaveViewPopover({
  dateWindow,
  resolvedRange,
  editing,
  onSave,
  onCancel,
}: SaveViewPopoverProps) {
  const canRoll = dateWindow?.kind === "rolling";
  // "Save what I see" is the least surprising default, and the common case.
  const [mode, setMode] = useState<WindowMode>(canRoll ? "rolling" : "fixed");
  const [name, setName] = useState(editing?.name ?? "");
  const [replaceSlice, setReplaceSlice] = useState(false);
  // Radios outside a form are grouped by name alone, so two popovers open at
  // once would share one group and silently uncheck each other.
  const modeGroup = useId();

  // Nothing to adopt when the page is already on the view's slice — sending it
  // back is a no-op, which is what makes the window choice safe to offer there.
  const takesPageSlice = !editing || replaceSlice || !editing.sliceDiffers;
  const isChoice = canRoll && !!resolvedRange && takesPageSlice;

  const submit = () => {
    const trimmed = name.trim();
    if (trimmed) onSave(trimmed, mode, takesPageSlice);
  };

  const frozenLabel = windowLabel({
    kind: "absolute",
    start: resolvedRange?.start_date,
    end: resolvedRange?.end_date,
  });

  return (
    <div
      role="dialog"
      aria-label={editing ? "Edit view" : "Save view"}
      className="absolute left-0 top-full mt-1 z-20 w-72 bg-popover border border-border rounded-lg shadow-md p-3"
    >
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") onCancel();
        }}
        placeholder="View name"
        className="w-full px-2 py-1.5 text-sm border border-input rounded-md bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      />

      {editing?.sliceDiffers && (
        <label className="flex items-start gap-2 mt-3 py-1 cursor-pointer">
          <input
            type="checkbox"
            checked={replaceSlice}
            onChange={(e) => setReplaceSlice(e.target.checked)}
            className="mt-1 accent-primary"
          />
          <span className="text-sm text-foreground">
            Update to the filters on screen
            <span className="block text-xs text-muted-foreground">
              replaces this view&apos;s filters, group-by and window
            </span>
          </span>
        </label>
      )}

      {isChoice && (
        <fieldset className="mt-3 border-0 p-0 m-0">
          <legend className="text-xs font-medium uppercase tracking-wide text-muted-foreground mb-1.5">
            Time window
          </legend>
          <label className="flex items-start gap-2 py-1 cursor-pointer">
            <input
              type="radio"
              name={modeGroup}
              checked={mode === "rolling"}
              onChange={() => setMode("rolling")}
              className="mt-1 accent-primary"
            />
            <span className="text-sm text-foreground">
              Rolling — {windowLabel(dateWindow)}
              <span className="block text-xs text-muted-foreground">
                updates every time you open this view
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 py-1 cursor-pointer">
            <input
              type="radio"
              name={modeGroup}
              checked={mode === "fixed"}
              onChange={() => setMode("fixed")}
              className="mt-1 accent-primary"
            />
            <span className="text-sm text-foreground">
              Fixed — {frozenLabel}
              <span className="block text-xs text-muted-foreground">
                frozen; always shows this period
              </span>
            </span>
          </label>
        </fieldset>
      )}

      <div className="flex justify-end gap-2 mt-3">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-xs font-medium text-muted-foreground border border-border rounded-md hover:bg-accent"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={!name.trim()}
          className="px-3 py-1 text-xs font-medium text-primary-foreground bg-primary rounded-md hover:bg-primary/90 disabled:bg-muted disabled:cursor-not-allowed"
        >
          {editing ? "Save changes" : "Save"}
        </button>
      </div>
    </div>
  );
}
